import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { formatPrice } from '../../../data/modules';
import { useModuleCatalog } from '../../../data/useModuleCatalog';

const SolutionOverview = () => {
  const { purchasableModules } = useModuleCatalog();
  const [activeTab, setActiveTab] = useState(purchasableModules?.[0]?.id);

  // Seçili modül katalogda satışa kapanmış olabilir; sekme boşta kalmasın.
  const activeSolution =
    purchasableModules.find((module) => module.id === activeTab) || purchasableModules?.[0];

  return (
    <section id="cozumler" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-poppins font-heading-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
            Kapsamlı{' '}
            <span className="text-primary">Dijital Çözümler</span>
          </h2>
          <p className="font-open-sans font-body-normal text-lg text-text-secondary max-w-3xl mx-auto">
            {purchasableModules?.length} modül, tek panel, tek veri. QR menü sürekli ücretsiz; kalan modülleri
            ihtiyacınız kadar açar, istediğiniz zaman kapatırsınız.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {purchasableModules.map((solution) => (
            <button
              key={solution.id}
              data-module-tab={solution.name}
              onClick={() => setActiveTab(solution.id)}
              className={`flex items-center space-x-3 px-5 py-3 rounded-full transition-all duration-300 ${
                activeTab === solution.id
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                activeTab === solution.id ? 'bg-white/20' : solution.color
              }`}>
                <Icon name={solution.icon} size={16} className="text-white" />
              </div>
              <div className="text-left">
                <div className="flex items-center space-x-2">
                  <span className="font-inter font-accent-semibold text-sm">{solution.name}</span>
                  {solution.badge && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      solution.badge === 'ÜCRETSİZ' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {solution.badge}
                    </span>
                  )}
                </div>
                <span className="font-open-sans font-body-normal text-xs opacity-75">{solution.subtitle}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Active Solution Content */}
        {activeSolution && (
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeSolution.color}`}>
                    <Icon name={activeSolution.icon} size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-poppins font-heading-bold text-2xl text-foreground">
                      {activeSolution.name}
                    </h3>
                    <p className="font-open-sans font-body-normal text-text-secondary">
                      {activeSolution.subtitle}
                    </p>
                  </div>
                </div>

                <p className="font-open-sans font-body-normal text-lg text-text-secondary leading-relaxed">
                  {activeSolution.description}
                </p>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeSolution.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={12} className="text-primary" />
                    </div>
                    <span className="font-inter font-accent-medium text-sm text-foreground">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* Highlights */}
              <div className="grid grid-cols-3 gap-4 pt-6">
                {activeSolution.highlights.map((highlight) => (
                  <div key={highlight.label} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="font-poppins font-heading-bold text-xl text-primary">
                      {highlight.value}
                    </p>
                    <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                      {highlight.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 pt-6">
                {activeSolution.demoLink && (
                  <a
                    href={activeSolution.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors duration-300"
                  >
                    <Icon name="ExternalLink" size={16} />
                    <span className="font-inter font-accent-semibold">
                      {activeSolution.ctaText}
                    </span>
                  </a>
                )}
                {activeSolution.path && (
                  <Link
                    to={activeSolution.path}
                    className="inline-flex items-center space-x-2 border border-primary text-primary px-6 py-3 rounded-lg hover:bg-primary/5 transition-colors duration-300"
                  >
                    <span className="font-inter font-accent-semibold">Modülü İncele</span>
                    <Icon name="ArrowRight" size={16} />
                  </Link>
                )}
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                <div className="bg-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeSolution.color}`}>
                        <Icon name={activeSolution.icon} size={16} className="text-white" />
                      </div>
                      <span className="font-poppins font-heading-semibold text-foreground">
                        {activeSolution.name}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  <div>
                    <p className="font-open-sans font-body-normal text-xs text-text-secondary uppercase tracking-wide mb-1">
                      Kimin için
                    </p>
                    <p className="font-inter font-accent-medium text-sm text-foreground">
                      {activeSolution.ideal}
                    </p>
                  </div>

                  <div className="flex items-end justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-poppins font-heading-bold text-3xl text-foreground">
                        {formatPrice(activeSolution.price.monthly)}
                      </p>
                      <p className="font-open-sans font-body-normal text-xs text-text-secondary">
                        {activeSolution.price.monthly === 0 ? 'Süresiz, koşulsuz' : 'aylık / işletme'}
                      </p>
                    </div>
                    {activeSolution.trialDays && (
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        {activeSolution.trialDays} gün ücretsiz
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {activeSolution.features.slice(0, 4).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                        <span className="font-open-sans font-body-normal text-xs text-text-secondary">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              {activeSolution.badge && (
                <div className="absolute -top-4 -right-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-lg ${
                    activeSolution.badge === 'ÜCRETSİZ'
                      ? 'bg-green-500 text-white'
                      : 'bg-orange-500 text-white'
                  }`}>
                    {activeSolution.badge}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Satışa kapalı modül — fiyat göstermeden, olduğu gibi */}
        <div className="mt-16 p-6 rounded-2xl border border-dashed border-amber-300 bg-amber-50/60 flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
            <Icon name="ShoppingBag" size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-poppins font-heading-semibold text-foreground">
              Pazaryeri Entegrasyonu <span className="text-amber-600 text-sm">(yakında)</span>
            </p>
            <p className="font-open-sans font-body-normal text-sm text-text-secondary">
              Yemeksepeti, Trendyol Go ve Getir siparişlerinin tek panele düşmesi. Platform anlaşmaları
              tamamlanana kadar satışa açmıyoruz — çalışmayan bir modülü satmak istemiyoruz.
            </p>
          </div>
          <Link
            to="/moduller"
            className="inline-flex items-center space-x-2 text-amber-700 font-inter font-accent-semibold text-sm whitespace-nowrap"
          >
            <span>Tüm modüller</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SolutionOverview;
